import {useLayoutEffect, useMemo} from 'react';
import type {FloatingWindowOpener, ModalsScope} from '@wix/devzai-utils-react';
import {DynamicRef} from '@wix/devzai-utils-react';
import {assertNotNullable, EventEmitter} from '@wix/devzai-utils-common';
import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import type {
    DoppeViewerAutoRedirectSettings
} from '../../components/doppe-viewer-auto-redirect-settings/doppe-viewer-auto-redirect-settings';
import type {
    DoppeViewerDefaultNotificationSettings
} from '../../components/doppe-viewer-default-notification-settings/doppe-viewer-default-notification-settings';
import type {DoppeViewerActionsList} from "../../components/doppe-viewer-actions-list/doppe-viewer-actions-list";
import {useDoppeViewerClientServices} from "../doppe-user-app/doppe-viewer-client-services-react";
import {DoppeViewerPageApi} from '../../components/doppe-viewer-page/doppe-viewer-page.types';
import type {DoppeViewerClientServices} from "../doppe-user-app/doppe-viewer-client-services";
import {DoppeViewerModalsController} from "../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller";

export interface DoppeViewerNavigationMenuController {
    openMenu(): void;
    closeMenu(): void;
    closeMenuImmediately(): void;
    isMenuOpen(): boolean;
}

export interface DoppeViewerAppUiServiceComponents {
    autoRedirectNotificationOpener: FloatingWindowOpener<DoppeViewerAutoRedirectSettings>;
    defaultNotificationOpener: FloatingWindowOpener<DoppeViewerDefaultNotificationSettings>;
    pageActionsList: DoppeViewerActionsList;
    pageNavigationMenuController: DoppeViewerNavigationMenuController;
    currentDoppeViewerPage: DoppeViewerPageApi;
    modalsScope: ModalsScope;
}

export class DoppeViewerAppUiService extends DoppeViewerAppClientServiceBase {

    private componentsInstances: Partial<DoppeViewerAppUiServiceComponents> = {};

    public setComponentInstance<COMPONENT extends keyof DoppeViewerAppUiServiceComponents>(
        componentName: COMPONENT,
        component: DoppeViewerAppUiServiceComponents[COMPONENT] | null
    ) {
        this.componentsInstances[componentName] = component ?? undefined;
    }

    public getComponentInstance<COMPONENT extends keyof DoppeViewerAppUiServiceComponents>(
        componentName: COMPONENT
    ) {
        return this.componentsInstances[componentName];
    }

    public assertComponentInstance<COMPONENT extends keyof DoppeViewerAppUiServiceComponents>(
        componentName: COMPONENT
    ) {
        return assertNotNullable(this.componentsInstances[componentName] as DoppeViewerAppUiServiceComponents[COMPONENT] | undefined, `Component '${componentName}' isn't registered`);
    }

    public get autoRedirectNotificationOpener() {
        return this.assertComponentInstance('autoRedirectNotificationOpener')
    }

    public get defaultNotificationOpener() {
        return this.assertComponentInstance('defaultNotificationOpener')
    }

    public get pageActionsList () {
        return this.getComponentInstance('pageActionsList')
    }

    public get pageNavigationMenuController () {
        return this.getComponentInstance('pageNavigationMenuController')
    }

    public get modalsScope () {
        return this.getComponentInstance('modalsScope')
    }

    public get currentDoppeViewerPage () {
        return this.getComponentInstance('currentDoppeViewerPage')
    }
}

export function useDoppeViewerAppUiServiceComponentRef<COMPONENT extends keyof DoppeViewerAppUiServiceComponents>(
    componentName: COMPONENT
) {

    const clientServices = useDoppeViewerClientServices();

    const instanceData = useMemo(() => {

        const instanceDynamicRef = DynamicRef.create<DoppeViewerAppUiServiceComponents[COMPONENT]>();
        const dynamicRefChangedEventBinding = EventEmitter.on(instanceDynamicRef.eventChanged, (componentInstance) => {
            clientServices.uiService.setComponentInstance(componentName, componentInstance);
        })

        return {
            instanceDynamicRef: instanceDynamicRef,
            destructor: () => {
                dynamicRefChangedEventBinding.dispose();
                clientServices.uiService.setComponentInstance(componentName, null);
            }
        }
    }, [clientServices, componentName])

    useLayoutEffect(() => {
        return () => {
            instanceData.destructor();
        }
    }, [instanceData]);

    return instanceData.instanceDynamicRef;
}

/**
 * Creates a DoppeViewerModalsController for the current page using the registered modalsScope
 * and unmountingTaskScheduler from the UI service.
 * 
 * This ensures modals opened via this controller will automatically close when the page changes.
 */
export function doppeViewerGetCurrentPageModals(
    doppeViewerClientServices: DoppeViewerClientServices
): DoppeViewerModalsController | null {
    const modalsScope = doppeViewerClientServices.uiService.modalsScope;
    if (!modalsScope) {
        return null;
    }

    const currentDoppeViewerPage = doppeViewerClientServices.uiService.currentDoppeViewerPage;

    return new DoppeViewerModalsController({
        modalsScope: modalsScope,
        unmountingTaskSchedulerGetter: currentDoppeViewerPage 
            ? (() => currentDoppeViewerPage.getUnmountingTaskScheduler()) 
            : null,
        doppeViewerPageApi: currentDoppeViewerPage ?? null
    });
}